import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, CircularProgress, Container, Divider, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LocaleContext } from "../../routing/LangRouter";

//import constants
import { IS_MOBILE } from "../../constants/breakpoints";

//import utils
import { convertToJalali } from "../../utils/date";

//import general components
import MobileHeader from "../../components/common/mobile-header/mobile-header";
import BaseTitle from "../../components/common/base-title/base-title";
import { BaseButton } from "../../components/common/base-button/base-button";

//import view components
import TourFlight from "../../components/view/tour-information/tour-flight/tour-flight";
import TourHotel from "../../components/view/tour-information/tour-hotel/tour-hotel";
import OtherInformation from "../../components/view/medical-tourism-result/other-information/other-information";
import TreatmentInformation from "../../components/view/medical-tourism-result/treatment-information/treatment-information";

//import images and icons
import { ArrowForward } from "iconsax-react";
import BASE_URL from "../../constants/baseUrls";
import axios from "axios";

function MedicalTourismResult() {
    const accessToken = localStorage.getItem("accessToken");
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [healthTourData, setHealthTourData] = useState({});

    const id = searchParams.get("id");
    useEffect(() => {
        fetchHealthTourData();
    }, [id]);

    const fetchHealthTourData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`${BASE_URL}/api/v2/itinerary/health/tour/list/?pk=${id}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (response.data && response.data.data) {
                setHealthTourData(response.data.data);
            } else {
                throw new Error("Unexpected data structure received from the server");
            }
        } catch (error) {
            console.error("Error fetching requests:", error);
            setError("شما هنوز درخواستی ثبت نکرده اید");
        } finally {
            setLoading(false);
        }
    };
    //general variables
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();

    //back to tour list
    const handleGoBack = () => {
        // navigate(`/${locale}/`);
    };

    // Conditional Rendering
    if (loading) {
        return (
            <Container maxWidth="xl" style={{ textAlign: "center", marginTop: "50px" }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" style={{ textAlign: "center", marginTop: "50px" }}>
                <Alert severity="error">{error}</Alert>
                <BaseButton onClick={handleGoBack} variant="contained" color="primary" style={{ marginTop: "20px" }}>
                    {t("goBack")}
                </BaseButton>
            </Container>
        );
    }

    const medicalInfos = healthTourData?.medical_infos || [];

    return (
        <Container maxWidth="xl">
            <MobileHeader title="نتیجه درخواست" justifyContent="center" handleClick={handleGoBack} />
            <Grid className="tour-information" container item xs={12} rowGap={5} mt={8} mb={5}>
                <Grid container item xs={12} rowGap={3} justifyContent="space-between" alignItems="flex-start">
                    {!IS_MOBILE && <BaseTitle title="اطلاعات درمان" dividerColor="1px solid var(--medium-gray-color)" />}
                    {healthTourData.medical_infos && healthTourData.medical_infos.length > 0 ? (
                        <TreatmentInformation medicalInfos={medicalInfos} />
                    ) : (
                        <Typography variant="body1">{t("noMedicalInfo")}</Typography>
                    )}
                </Grid>

                <Grid container item xs={12} rowGap={3}>
                    <BaseTitle
                        title={t("ticket")}
                        dividerColor="1px solid var(--medium-gray-color)"
                        button={
                            <Grid container item xs={12} columnGap={2} justifyContent="space-between">
                                <span className="flight-item__departure-flight">
                                    {t("departureDate")}:{" "}
                                    {locale === "fa"
                                        ? convertToJalali(healthTourData?.depart_flight?.info[0]?.departure_date)
                                        : healthTourData?.depart_flight?.info[0]?.departure_date}
                                </span>
                                |
                                <span className="flight-item__destination-flight">
                                    {t("returnDate")}:{" "}
                                    {locale === "fa"
                                        ? convertToJalali(healthTourData?.return_flight?.info[0]?.departure_date)
                                        : healthTourData?.return_flight?.info[0]?.departure_date}
                                </span>
                            </Grid>
                        }
                    />

                    <Grid className="flight-item" container item xs={12} rowGap={2} p={{ xs: 2, md: 4 }}>
                        <TourFlight
                            title={t("departureFlight")}
                            flightClass={healthTourData.depart_flight?.info[0]?.cobin_name}
                            airline={[
                                {
                                    id: 1, // This can be dynamic
                                    code: healthTourData.depart_flight?.info[0]?.airline,
                                    name: healthTourData.depart_flight?.info[0]?.airline_name,
                                    english_name: healthTourData.depart_flight?.info[0]?.airline_name,
                                    logo: healthTourData.depart_flight?.info[0]?.airline_logo,
                                },
                            ]}
                            departure={{
                                id: 1, // Dynamic value if available
                                name: healthTourData?.depart_flight?.info[0]?.departure_airport.name,
                                english_name: healthTourData?.depart_flight?.info[0]?.departure_airport.english_name,
                                code: healthTourData?.depart_flight?.info[0]?.departure_airport.code,
                            }}
                            destination={{
                                id: 1, // Dynamic value if available
                                name: healthTourData?.depart_flight?.info[0]?.destination_airport.name,
                                english_name: healthTourData?.depart_flight?.info[0]?.destination_airport.english_name,
                                code: healthTourData?.depart_flight?.info[0]?.destination_airport.code,
                            }}
                            duration={healthTourData?.depart_flight?.info[0]?.duration}
                            departureTime={healthTourData?.depart_flight?.info[0]?.departure_date}
                            arrivedTime={healthTourData?.depart_flight?.info[0]?.destination_date}
                        />
                        <Divider width="100%" />
                        <TourFlight
                            title={t("returnFlight")}
                            flightClass={healthTourData?.return_flight?.info[0]?.cobin_name}
                            airline={[
                                {
                                    id: 1, // This can be dynamic
                                    code: healthTourData?.return_flight?.info[0]?.airline,
                                    name: healthTourData?.return_flight?.info[0]?.airline_name,
                                    english_name: healthTourData?.return_flight?.info[0]?.airline_name,
                                    logo: healthTourData?.return_flight?.info[0]?.airline_logo,
                                },
                            ]}
                            departure={{
                                id: 1, // Dynamic value if available
                                name: healthTourData?.return_flight?.info[0]?.departure_airport.name,
                                english_name: healthTourData?.return_flight?.info[0]?.departure_airport.english_name,
                                code: healthTourData?.return_flight?.info[0]?.departure_airport.code,
                            }}
                            destination={{
                                id: 1, // Dynamic value if available
                                name: healthTourData?.return_flight?.info[0]?.destination_airport.name,
                                english_name: healthTourData?.return_flight?.info[0]?.destination_airport.english_name,
                                code: healthTourData?.return_flight?.info[0]?.destination_airport.code,
                            }}
                            duration={healthTourData?.return_flight?.info[0]?.duration}
                            departureTime={healthTourData?.return_flight?.info[0]?.departure_date}
                            arrivedTime={healthTourData?.return_flight?.info[0]?.destination_date}
                        />
                    </Grid>
                </Grid>

                <Grid container item xs={12} rowGap={3} columnGap={3}>
                    <BaseTitle title={t("hotel")} dividerColor="1px solid var(--medium-gray-color)" />

                    <Grid container item md={3.85}>
                        <TourHotel
                            hotelItem={{
                                id: 11,
                                accommodation: {
                                    id: 1385,
                                    name: healthTourData?.accommodation?.accommodation?.name,
                                    english_name: healthTourData?.accommodation?.accommodation?.english_name,
                                    address: healthTourData?.accommodation?.accommodation?.address,
                                    website: healthTourData?.accommodation?.accommodation?.website,
                                    star: healthTourData?.accommodation?.accommodation?.star,
                                    star_rating: healthTourData?.accommodation?.accommodation?.star_rating,
                                    latitude: healthTourData?.accommodation?.accommodation?.latitude,
                                    longitude: healthTourData?.accommodation?.accommodation?.longitude,
                                    zone: healthTourData?.accommodation?.accommodation?.zone,
                                    access_type: healthTourData?.accommodation?.accommodation?.access_type,
                                },
                                stay_nights: healthTourData?.accommodation?.accommodation?.star_nights,
                            }}
                        />
                    </Grid>
                </Grid>

                <Grid container item xs={12} rowGap={3} columnGap={3}>
                    <BaseTitle title="اطلاعات دیگر" dividerColor="1px solid var(--medium-gray-color)" />
                    <OtherInformation healthTourData={healthTourData} />
                </Grid>
            </Grid>
        </Container>
    );
}

export default MedicalTourismResult;
// import { useContext, useEffect, useState } from "react";
// import { useNavigate, useSearchParams } from "react-router-dom";
// import { Container, Divider, Grid, Typography } from "@mui/material";
// import { useTranslation } from "react-i18next";
// import { LocaleContext } from "../../routing/LangRouter";

// //import view components
// import TourFlight from "../../components/view/tour-information/tour-flight/tour-flight";
// import TourHotel from "../../components/view/tour-information/tour-hotel/tour-hotel";
// import OtherInformation from "../../components/view/medical-tourism-result/other-information/other-information";
// import TreatmentInformation from "../../components/view/medical-tourism-result/treatment-information/treatment-information";

// //import images and icons
// import { ArrowForward } from "iconsax-react";
// import BASE_URL from "../../constants/base-urls";
// import axios from "axios";
// import MobileHeader from "../../components/common/mobile-header/mobile-header";
// import { IS_MOBILE } from "../../constants/breakpoints";
// import BaseTitle from "../../components/common/base-title/base-title";
// import { convertToJalali } from "../../utils/date";

// const accessToken = localStorage.getItem("accessToken");

// function MedicalTourismResult() {
//     const [searchParams] = useSearchParams();
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [requestData, setRequestData] = useState(null);

//     const id = searchParams.get("id");
//     useEffect(() => {
//         fetchRequest();
//     }, []);

//     const fetchRequest = async () => {
//         try {
//             setLoading(true);
//             setError(null);
//             const response = await axios.get(`${BASE_URL}/api/v2/itinerary/health/tour/list/?pk=${id}`, {
//                 headers: {
//                     Authorization: `Bearer ${accessToken}`,
//                 },
//             });
//             if (response.data && response.data.data) {
//                 setRequestData(response.data.data);
//             } else {
//                 throw new Error("Unexpected data structure received from the server");
//             }
//         } catch (error) {
//             console.error("Error fetching request:", error);
//             setError("Failed to load request data");
//         } finally {
//             setLoading(false);
//         }
//     };

//     const navigate = useNavigate();
//     const { locale } = useContext(LocaleContext);
//     const { t } = useTranslation();

//     const handleGoBack = () => {
//         // navigate(`/${locale}/`);
//     };

//     if (loading) return <Typography>Loading...</Typography>;
//     if (error) return <Typography color="error">{error}</Typography>;
//     if (!requestData) return null;

//     return (
//         <Container maxWidth="xl">
//             <MobileHeader title="نتیجه درخواست" justifyContent="center" handleClick={handleGoBack} />
//             <Grid className="tour-information" container item xs={12} rowGap={5} mt={8} mb={5}>
//                 <Grid container item xs={12} rowGap={3} justifyContent="space-between" alignItems="flex-start">
//                     {!IS_MOBILE && <BaseTitle title="اطلاعات درمان" dividerWidth="70%" dividerColor="1px solid var(--medium-gray-color)" />}

//                     <TreatmentInformation
//                         services={requestData.services}
//                         medicalInfos={requestData.medical_infos}
//                         illnessDescription={requestData.illness_description}
//                     />
//                 </Grid>

//                 <Grid container item xs={12} rowGap={3}>
//                     <BaseTitle
//                         title={t("ticket")}
//                         dividerWidth="62%"
//                         dividerColor="1px solid var(--medium-gray-color)"
//                         button={
//                             <Grid container item xs={12} columnGap={2} justifyContent="space-between">
//                                 <span className="flight-item__departure-flight">
//                                     {t("requestDate")}: {locale === "fa" ? convertToJalali(requestData.request_date) : requestData.request_date}
//                                 </span>
//                                 {requestData.finish_date && (
//                                     <>
//                                         |
//                                         <span className="flight-item__destination-flight">
//                                             {t("finishDate")}: {locale === "fa" ? convertToJalali(requestData.finish_date) : requestData.finish_date}
//                                         </span>
//                                     </>
//                                 )}
//                             </Grid>
//                         }
//                     />

//                     <Grid className="flight-item" container item xs={12} rowGap={2} p={{ xs: 2, md: 4 }}>
//                         {requestData.depart_flight ? (
//                             <TourFlight
//                                 title={t("departureFlight")}
//                                 flightClass={requestData.preferred_flight_class}
//                                 airline={requestData.depart_flight.airline}
//                                 departure={requestData.depart_flight.departure}
//                                 destination={requestData.depart_flight.destination}
//                                 duration={requestData.depart_flight.duration}
//                                 departureTime={requestData.depart_flight.departureTime}
//                                 arrivedTime={requestData.depart_flight.arrivedTime}
//                             />
//                         ) : (
//                             <Typography>{t("noFlightInfo")}</Typography>
//                         )}
//                         {requestData.depart_flight && requestData.return_flight && <Divider width="100%" />}
//                         {requestData.return_flight ? (
//                             <TourFlight
//                                 title={t("returnFlight")}
//                                 flightClass={requestData.preferred_flight_class}
//                                 airline={requestData.return_flight.airline}
//                                 departure={requestData.return_flight.departure}
//                                 destination={requestData.return_flight.destination}
//                                 duration={requestData.return_flight.duration}
//                                 departureTime={requestData.return_flight.departureTime}
//                                 arrivedTime={requestData.return_flight.arrivedTime}
//                             />
//                         ) : (
//                             <Typography>{t("noFlightInfo")}</Typography>
//                         )}
//                     </Grid>
//                 </Grid>

//                 <Grid container item xs={12} rowGap={3} columnGap={3}>
//                     <BaseTitle title={t("hotel")} dividerWidth="90%" dividerColor="1px solid var(--medium-gray-color)" />

//                     <Grid container item md={3.85}>
//                         {requestData.accommodation ? (
//                             <TourHotel hotelItem={requestData.accommodation} />
//                         ) : (
//                             <Typography>{t("noHotelInfo")}</Typography>
//                         )}
//                     </Grid>
//                 </Grid>

//                 <Grid container item xs={12} rowGap={3} columnGap={3}>
//                     <BaseTitle title="اطلاعات دیگر" dividerWidth="90%" dividerColor="1px solid var(--medium-gray-color)" />
//                     <OtherInformation requestData={requestData} />
//                 </Grid>
//             </Grid>
//         </Container>
//     );
// }

// export default MedicalTourismResult;
